import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Lead } from '@/types/lead';

// `slaDeadline` is a real column on the lead record and is returned by the API
// (the service spreads `...lead`), but it isn't declared on the shared `Lead`
// type. Read it through a narrow local shape rather than widening that type.
type LeadWithSlaDeadline = Lead & { slaDeadline?: string | null };

// Within this window of the deadline the countdown turns amber ("running out").
const SLA_WARNING_SECONDS = 15 * 60;

// Human-readable duration: "1h 4m" / "12m" / "45s". Rounds down, never negative.
function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${s}s`;
}

// Seconds it took to first-respond, preferring the backend-computed value and
// falling back to firstResponseAt − createdAt when it isn't present.
function firstResponseSeconds(lead: Lead): number | null {
  if (lead.timeToFirstResponse != null) return lead.timeToFirstResponse;
  if (lead.firstResponseAt) {
    return Math.round(
      (new Date(lead.firstResponseAt).getTime() - new Date(lead.createdAt).getTime()) / 1000,
    );
  }
  return null;
}

/**
 * Live SLA indicator for a lead (spec 7.2).
 *
 *   - closed lead            → renders nothing
 *   - first response logged  → green check + "Responded in Xm"
 *   - otherwise counts down from `slaDeadline`, ticking every second:
 *       green  when there's plenty of time,
 *       amber  as the deadline approaches,
 *       red    + "SLA BREACHED" once past it.
 *
 * Falls back to a neutral "Awaiting response" pill when no deadline is set.
 */
export function SlaCountdown({ lead, className }: { lead: Lead; className?: string }) {
  const responded = Boolean(lead.firstResponseAt);
  const closed = Boolean(lead.isClosed);
  const slaDeadline = (lead as LeadWithSlaDeadline).slaDeadline ?? null;

  // Re-render each second only while an active countdown is on screen — closed
  // or already-responded cards don't need a ticking clock.
  const ticking = !closed && !responded && slaDeadline != null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ticking]);

  // Closed leads carry no live SLA meaning.
  if (closed) return null;

  const pill = 'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium';

  if (responded) {
    const seconds = firstResponseSeconds(lead);
    return (
      <span className={cn(pill, 'bg-emerald-50 text-emerald-700', className)}>
        <CheckCircle2 size={8} />
        {seconds != null ? `Responded in ${formatDuration(seconds)}` : 'Responded'}
      </span>
    );
  }

  if (slaDeadline == null) {
    return (
      <span className={cn(pill, 'bg-amber-50 text-amber-700', className)}>
        <Clock size={8} />
        Awaiting response
      </span>
    );
  }

  const remainingSeconds = (new Date(slaDeadline).getTime() - now) / 1000;
  const breached = lead.slaBreached || remainingSeconds <= 0;

  if (breached) {
    return (
      <span className={cn(pill, 'bg-red-50 text-red-700', className)}>
        <AlertTriangle size={8} />
        SLA BREACHED
      </span>
    );
  }

  const nearDeadline = remainingSeconds <= SLA_WARNING_SECONDS;
  return (
    <span
      className={cn(
        pill,
        nearDeadline ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700',
        className,
      )}
    >
      <Clock size={8} />
      SLA {formatDuration(remainingSeconds)}
    </span>
  );
}
